import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import Navbar from '@/components/feature/Navbar'
import Footer from '@/components/feature/Footer'
import { allCategories } from '@/mocks/categories'
import { getComicsByCategory, comics } from '@/mocks/comics'
import type { Comic } from '@/mocks/comics'

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>()
  const [activeSlug, setActiveSlug] = useState(slug || 'romance')
  const [sortBy, setSortBy] = useState<'popular' | 'newest' | 'rating'>('popular')

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [activeSlug])

  useEffect(() => {
    if (slug) setActiveSlug(slug)
  }, [slug])

  const currentCategory = allCategories.find(c => c.slug === activeSlug) || allCategories[0]

  let categoryComics = getComicsByCategory(activeSlug)
  if (categoryComics.length === 0) {
    categoryComics = comics.slice(0, 6)
  }

  if (sortBy === 'rating') {
    categoryComics = [...categoryComics].sort((a, b) => b.rating - a.rating)
  } else if (sortBy === 'newest' && slug !== 'romance') {
    categoryComics = [...categoryComics].sort((a, b) => b.chapters - a.chapters)
  }

  return (
    <>
      <Navbar />

      <main>
        <section className="pt-24 pb-8 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors duration-200 cursor-pointer">首页</Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              <span className="text-foreground-700 font-medium">{currentCategory.name}</span>
            </nav>

            <div className="mb-6">
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium mb-3">
                {currentCategory.count.toLocaleString()}部作品
              </span>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-2">
                {currentCategory.name}
              </h1>
              <p className="text-sm text-foreground-500">{currentCategory.description}</p>
            </div>

            <div className="flex items-center gap-2 overflow-x-auto pb-2 -mx-1 px-1">
              {allCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-foreground-900 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className={`${cat.icon} text-[11px]`}></i>
                  </span>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-8 md:py-12 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-foreground-500">
                共 <strong className="text-foreground-900">{categoryComics.length}</strong> 部作品
              </p>
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                {[
                  { key: 'popular' as const, label: '最热' },
                  { key: 'newest' as const, label: '最新' },
                  { key: 'rating' as const, label: '评分' },
                ].map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setSortBy(s.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      sortBy === s.key
                        ? 'bg-background-50 text-foreground-900 shadow-sm'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {categoryComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>

            {categoryComics.length === 0 && (
              <div className="text-center py-20">
                <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mx-auto mb-3">
                  <i className="ri-emotion-sad-line text-xl"></i>
                </span>
                <p className="text-foreground-500 text-sm">该分类暂无作品，请浏览其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `${currentCategory.name} - 韩漫漫画网站`,
        description: `${currentCategory.description}，共${currentCategory.count}部作品`,
        url: `http://tenford.cn/category/${activeSlug}`,
        isPartOf: {
          '@type': 'WebSite',
          name: '韩漫漫画网站',
          url: 'http://tenford.cn/',
        },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'http://tenford.cn/' },
            { '@type': 'ListItem', position: 2, name: currentCategory.name, item: `http://tenford.cn/category/${activeSlug}` },
          ],
        },
      })}} />
    </>
  )
}

function ComicCard({ comic }: { comic: Comic }) {
  return (
    <Link
      to={`/article/${comic.id}`}
      className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200/50 hover:border-primary-200 transition-all duration-300 cursor-pointer"
    >
      <div className="relative w-full overflow-hidden" style={{ height: '240px' }}>
        <img
          src={comic.cover}
          alt={comic.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute top-3 left-3">
          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${
            comic.status === '连载中' ? 'bg-primary-500 text-background-50' : 'bg-foreground-900/70 text-background-50'
          }`}>
            {comic.status}
          </span>
        </div>
        {comic.isHot && (
          <div className="absolute top-3 right-3">
            <span className="inline-block px-2 py-0.5 bg-accent-500 text-background-50 rounded-full text-[10px] font-semibold">
              HOT
            </span>
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-heading text-sm font-bold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 mb-1 line-clamp-1">
          {comic.title}
        </h3>
        <p className="text-xs text-foreground-500 mb-2">{comic.author}</p>
        <div className="flex items-center justify-between">
          <span className="text-xs text-foreground-400">{comic.updateInfo}</span>
          <span className="flex items-center gap-0.5 text-xs font-medium text-foreground-700">
            <span className="w-3.5 h-3.5 flex items-center justify-center text-accent-500">
              <i className="ri-star-fill text-[10px]"></i>
            </span>
            {comic.rating}
          </span>
        </div>
      </div>
    </Link>
  )
}